<template>
  <!--
    Outer padding = thumbRadius (10px). All visual elements are positioned with
    plain `pct * 100%` inside the content area. The native input is stretched
    outward by the same amount so browser thumb centers match exactly.
  -->
  <div
    ref="rootRef"
    data-testid="range-slider"
    class="relative w-full px-2.5"
    :class="thumbLabel === 'always' ? 'pt-8' : ''"
    :style="cssVars"
  >
    <div
      class="relative h-5"
      @pointerenter="hovered = true"
      @pointerleave="hovered = false"
    >
      <!-- Track -->
      <div
        class="pointer-events-none absolute inset-x-0 top-1/2 h-1.5 -translate-y-1/2 rounded-full bg-depth-3"
      >
        <!-- Brand fill: 0 → thumb (or 0 → delimiter when above it) -->
        <div
          class="absolute inset-y-0 left-0 rounded-full bg-brand"
          :style="{ width: brandFillWidth }"
        ></div>

        <!-- Cloud fill: delimiter → thumb -->
        <div
          v-if="hasDelimiter && isAboveDelimiter"
          class="absolute inset-y-0 rounded-full"
          style="
            background: color-mix(
              in srgb,
              var(--color-brand) 35%,
              var(--color-depth-3)
            );
          "
          :style="{ left: delimiterLeft, width: cloudFillWidth }"
        ></div>
      </div>

      <!-- Delimiter line -->
      <div
        v-if="hasDelimiter"
        class="pointer-events-none absolute inset-y-0 w-px -translate-x-1/2 rounded-full bg-mouse/40"
        :style="{ left: delimiterLeft }"
      ></div>

      <!-- Thumb label bubble — positioned above the thumb center -->
      <div
        class="pointer-events-none absolute"
        :style="{ left: thumbLeft, bottom: 'calc(100% + 6px)' }"
      >
        <transition name="thumb-label">
          <div
            v-if="showThumbLabel"
            class="thumb-label-bubble relative -translate-x-1/2 whitespace-nowrap rounded border border-depth-3 bg-depth-0 px-2 py-0.5 text-xs font-semibold text-mouse shadow-sm"
          >
            <slot name="thumb-label">{{ liveValue }}</slot>
            <!-- Downward arrow -->
            <div
              class="absolute bottom-0 left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rotate-45 border-b border-r border-depth-3 bg-depth-0"
            ></div>
          </div>
        </transition>
      </div>

      <!-- Thumb (visual only) -->
      <div
        class="pointer-events-none absolute top-1/2 size-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-depth-2 bg-brand shadow-md transition-transform duration-100"
        :class="dragging ? 'scale-110' : 'scale-100'"
        :style="{ left: thumbLeft }"
      ></div>

      <!--
        Native input: stretched to full outer width (content area + 2×padding)
        so the browser's internal thumb positions align with our visual thumb.

        @change is intentionally OMITTED — Firefox fires it on every mousemove
        during drag, which would trigger API calls on each pixel moved.
        Commits are handled by @pointerup (mouse/touch release) and @keyup
        (keyboard arrow keys) instead.
      -->
      <input
        type="range"
        :aria-label="label"
        :min="min"
        :max="max"
        :step="step"
        :value="modelValue"
        :disabled="disabled"
        class="absolute inset-y-0 -left-2.5 -right-2.5 h-full w-[calc(100%+20px)] cursor-pointer opacity-0 disabled:cursor-not-allowed"
        @input="onInput"
        @pointerdown="onPointerDown"
        @pointerup="onPointerUp"
        @keyup="onKeyUp"
      />
    </div>

    <!-- Ticks slot — receives tickLeft(value) which returns a CSS left string -->
    <div v-if="$slots.ticks" class="relative mt-1 h-6 select-none text-sm">
      <slot name="ticks" :tick-left="tickLeft" />
    </div>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    modelValue: number;
    label: string;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    /** Optional split point: track left is brand, right is cloud-tinted */
    delimiter?: number;
    /**
     * Controls the thumb value bubble:
     * - `true`      → visible on hover and while dragging (works on mobile touch)
     * - `'always'`  → always visible (adds top padding to reserve space)
     * - `false`/omitted → never shown
     */
    thumbLabel?: boolean | 'always';
  }>(),
  {
    min: 0,
    max: 100,
    step: 1,
    disabled: false,
    delimiter: undefined,
    thumbLabel: false,
  },
);

const emit = defineEmits<{
  'update:modelValue': [value: number];
  /** Fires on pointer/touch release or keyboard navigation — safe for API calls */
  change: [value: number];
}>();

const rootRef = ref<HTMLElement>();
const dragging = ref(false);
const hovered = ref(false);
// Live value tracked locally during drag to update the thumb label and CSS vars
// without propagating through the parent's reactivity chain every frame.
const liveValue = ref(props.modelValue);

const showThumbLabel = computed(() => {
  if (!props.thumbLabel) return false;
  if (props.thumbLabel === 'always') return true;
  return dragging.value || hovered.value;
});

function rawPct(value: number): number {
  const range = props.max - props.min;
  return range <= 0 ? 0 : (value - props.min) / range;
}

// CSS variables drive all visual positions; updated directly on the root DOM
// node during drag to skip the Vue reactivity → parent re-render cycle.
const cssVars = computed(() => ({
  '--pct': rawPct(props.modelValue),
  '--delim-pct': props.delimiter !== undefined ? rawPct(props.delimiter) : 0,
}));

function setCssVar(pct: number): void {
  rootRef.value?.style.setProperty('--pct', String(pct));
}

const hasDelimiter = computed(
  () =>
    props.delimiter !== undefined &&
    props.delimiter > props.min &&
    props.delimiter < props.max,
);

const isAboveDelimiter = computed(
  () => props.delimiter !== undefined && liveValue.value > props.delimiter,
);

const thumbLeft = 'calc(var(--pct) * 100%)';
const delimiterLeft = 'calc(var(--delim-pct) * 100%)';

const brandFillWidth = computed(() =>
  hasDelimiter.value && isAboveDelimiter.value ? delimiterLeft : thumbLeft,
);
const cloudFillWidth = 'calc((var(--pct) - var(--delim-pct)) * 100%)';

/** CSS left string for positioning a tick label at the given value */
function tickLeft(value: number): string {
  return `${rawPct(value) * 100}%`;
}

function onInput(event: Event): void {
  const val = Number((event.target as HTMLInputElement).value);
  liveValue.value = val;
  // Update CSS variable directly — bypasses the parent re-render on every frame
  setCssVar(rawPct(val));
  emit('update:modelValue', val);
}

function onPointerDown(): void {
  dragging.value = true;
}

function onPointerUp(event: PointerEvent): void {
  dragging.value = false;
  const val = Number((event.target as HTMLInputElement).value);
  emit('change', val);
}

// Keyboard: arrow keys fire input (moves value) but no pointerup — commit here
const KEY_COMMIT = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
]);
function onKeyUp(event: KeyboardEvent): void {
  if (KEY_COMMIT.has(event.key)) {
    emit('change', Number((event.target as HTMLInputElement).value));
  }
}

// Keep liveValue in sync when the parent resets/changes the value externally
watch(
  () => props.modelValue,
  (val) => {
    if (!dragging.value) liveValue.value = val;
  },
);
</script>

<style scoped>
/* Thumb label fade + slight upward slide */
.thumb-label-enter-active,
.thumb-label-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}

.thumb-label-enter-from,
.thumb-label-leave-to {
  opacity: 0;
  --tw-translate-y: 3px;
}
</style>
